import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable, useDroppable } from '@dnd-kit/core';

import { cn } from '@/lib/utils';
// Leaf imports, not the kit's barrel: the barrel brings in AdminPage and with it the app shell.
import { StatusBadge } from '../../../components/design-system/admin/StatusBadge';
import type { StatusTone } from '../../../components/design-system/admin/StatusBadge';
import { formatDate } from '../../../components/design-system/admin/format';
import { QuoteRequest, QuoteStatus } from '../../../common/models/billing.model';
import { formatMoney, sumByCurrency } from './money';

export const STATUS_ORDER: QuoteStatus[] = ['new', 'contacted', 'won', 'lost'];

/** The pipeline reads new (ink) → contacted (warning) → won (success) / lost (grey). */
export const STATUS_META: Record<QuoteStatus, { label: string; tone: StatusTone }> = {
  new: { label: 'ADMIN_SALES.STATUS.NEW', tone: 'info' },
  contacted: { label: 'ADMIN_SALES.STATUS.CONTACTED', tone: 'warning' },
  won: { label: 'ADMIN_SALES.STATUS.WON', tone: 'success' },
  lost: { label: 'ADMIN_SALES.STATUS.LOST', tone: 'neutral' },
};

/** Plan feature keys → ADMIN_SALES.FEATURES.<key>. Unknown keys show as they are. */
export const FEATURE_KEYS: Record<string, string> = {
  apiIntegration: 'API_INTEGRATION',
  emailAutomation: 'EMAIL_AUTOMATION',
  aiSpaceAnalysis: 'AI_SPACE_ANALYSIS',
  seamlessGeneration: 'SEAMLESS_GENERATION',
  model3dGeneration: 'MODEL_3D_GENERATION',
  advancedAnalytics: 'ADVANCED_ANALYTICS',
  customFields: 'CUSTOM_FIELDS',
  categoryManagement: 'CATEGORY_MANAGEMENT',
  productCardCustomization: 'PRODUCT_CARD_CUSTOMIZATION',
  contentStudio: 'CONTENT_STUDIO',
  socialScheduler: 'SOCIAL_SCHEDULER',
};

/** Plan limit keys → ADMIN_SALES.VOLUMES.<key>. Unknown keys show as they are. */
export const VOLUME_KEYS: Record<string, string> = {
  maxProducts: 'MAX_PRODUCTS',
  maxSpaces: 'MAX_SPACES',
  maxEmailsPerMonth: 'MAX_EMAILS_PER_MONTH',
  maxSyncFlowsPerDay: 'MAX_SYNC_FLOWS_PER_DAY',
  maxAutomationRules: 'MAX_AUTOMATION_RULES',
  maxLeads: 'MAX_LEADS',
  maxImportProducts: 'MAX_IMPORT_PRODUCTS',
  monthlyCredits: 'MONTHLY_CREDITS',
  maxSocialAccounts: 'MAX_SOCIAL_ACCOUNTS',
  maxScheduledPostsPerMonth: 'MAX_SCHEDULED_POSTS_PER_MONTH',
  maxVisualizationsPerMonth: 'MAX_VISUALIZATIONS_PER_MONTH',
  maxSeats: 'MAX_SEATS',
};

const hasAmount = (amount?: number | null): amount is number =>
  amount !== undefined && amount !== null && !Number.isNaN(Number(amount));

/** What a set of quotes is worth a year, per currency: monthly prices × 12, unpriced quotes left out. */
export function annualQuoteValue(quotes: QuoteRequest[]): Map<string, number> {
  return sumByCurrency(
    quotes.filter((q) => hasAmount(q.quotedAmount)),
    (q) => Number(q.quotedAmount) * (q.quotedInterval === 'month' ? 12 : 1),
    (q) => q.quotedCurrency,
  );
}

/** "$4,990 / year" as plain text (for sentences and toasts); null when no amount is set. */
export function usePriceText() {
  const { t } = useTranslation();
  return (amount?: number | null, currency?: string | null, interval?: string | null): string | null => {
    if (!hasAmount(amount)) return null;
    const money = formatMoney(Number(amount), currency || 'usd');
    if (!interval) return money;
    return `${money} ${t(interval === 'year' ? 'ADMIN_SALES.PRICE.PER_YEAR' : 'ADMIN_SALES.PRICE.PER_MONTH')}`;
  };
}

/** A quoted price: the amount, then the interval in grey. Renders nothing without an amount. */
export function QuotePrice({
  amount,
  currency,
  interval,
  className,
}: {
  amount?: number | null;
  currency?: string | null;
  interval?: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!hasAmount(amount)) return null;
  return (
    <span className={cn('inline-flex items-baseline gap-1 tabular-nums', className)}>
      <span>{formatMoney(Number(amount), currency || 'usd')}</span>
      {interval && (
        <span className="text-muted-foreground">
          {t(interval === 'year' ? 'ADMIN_SALES.PRICE.PER_YEAR' : 'ADMIN_SALES.PRICE.PER_MONTH')}
        </span>
      )}
    </span>
  );
}

// ── Draggable card ──────────────────────────────────────────────────────────
export const QuoteCard: React.FC<{ quote: QuoteRequest; onOpen: (q: QuoteRequest) => void }> = ({ quote, onOpen }) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: quote._id });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.35 : 1,
  };
  const features = quote.selectedFeatures?.length || 0;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={() => onOpen(quote)}
      data-testid={`quote-card-${quote._id}`}
      className="cursor-grab rounded-lg border bg-card p-3 text-start transition-colors select-none hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none active:cursor-grabbing"
    >
      <p className="truncate text-sm font-medium">{quote.vendorName}</p>
      {quote.planName && <p className="mt-0.5 truncate text-xs text-muted-foreground">{quote.planName}</p>}
      <QuotePrice
        amount={quote.quotedAmount}
        currency={quote.quotedCurrency}
        interval={quote.quotedInterval}
        className="mt-2 text-sm font-medium"
      />
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <span>{t('ADMIN_SALES.CARD.FEATURES', { count: features })}</span>
        <span className="ms-auto tabular-nums">{formatDate(quote.createdAt)}</span>
      </div>
    </div>
  );
};

/** What a card looks like while it is being dragged. */
export const QuoteCardOverlay: React.FC<{ quote: QuoteRequest }> = ({ quote }) => (
  <div className="cursor-grabbing rounded-lg border bg-card p-3 shadow-md">
    <p className="text-sm font-medium">{quote.vendorName}</p>
    {quote.planName && <p className="text-xs text-muted-foreground">{quote.planName}</p>}
  </div>
);

// ── Droppable column ────────────────────────────────────────────────────────
export const KanbanColumn: React.FC<{ status: QuoteStatus; quotes: QuoteRequest[]; onOpen: (q: QuoteRequest) => void }> = ({ status, quotes, onOpen }) => {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const meta = STATUS_META[status];
  return (
    <div
      ref={setNodeRef}
      data-testid={`column-${status}`}
      className={cn(
        'flex min-h-80 flex-col rounded-xl p-2 transition-colors',
        isOver ? 'bg-accent ring-1 ring-foreground/20' : 'bg-muted/60',
      )}
    >
      <div className="flex items-center gap-2 px-1.5 pt-1 pb-2.5">
        <StatusBadge tone={meta.tone} className="text-sm font-medium">{t(meta.label)}</StatusBadge>
        <span className="ms-auto text-xs text-muted-foreground tabular-nums">{quotes.length}</span>
      </div>
      <div className="flex max-h-[calc(100vh-320px)] flex-1 flex-col gap-2 overflow-y-auto">
        {quotes.map((q) => (
          <QuoteCard key={q._id} quote={q} onOpen={onOpen} />
        ))}
        {quotes.length === 0 && (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed py-8">
            <p className="text-xs text-muted-foreground">{t('ADMIN_SALES.QUOTES.EMPTY_COLUMN')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
